import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Trash2, Clock, DoorOpen, Loader2 } from "lucide-react";

export default function RoomsTab() {
  const [config, setConfig] = useState({ sedi: [], rooms: {} });
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [sede, setSede] = useState("Studio Milano");
  const [shifts, setShifts] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ room: "", start_time: "09:00", end_time: "11:00", collaborator_name: "", note: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, cl] = await Promise.all([
        api.get("/public/config"),
        api.get("/room-shifts", { params: { date } }),
        api.get("/collaborators"),
      ]);
      setConfig(c.data);
      setShifts(s.data);
      setCollaborators(cl.data);
    } catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const rooms = config.rooms?.[sede] || [];

  const openFor = (room) => {
    setForm({ room, start_time: "09:00", end_time: "11:00", collaborator_name: "", note: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.collaborator_name) { toast.error("Seleziona un collaboratore"); return; }
    setSaving(true);
    try {
      await api.post("/room-shifts", { ...form, sede, date });
      toast.success("Turno assegnato");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/room-shifts/${id}`); setShifts((s) => s.filter((x) => x.id !== id)); toast.success("Turno rimosso"); }
    catch (e) { toast.error("Errore"); }
  };

  const TIMES = ["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00"];

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end mb-6">
        <div className="space-y-1.5">
          <Label className="text-stone-600 text-xs">Data</Label>
          <Input type="date" value={date} data-testid="rooms-date-picker" onChange={(e) => setDate(e.target.value)}
            className="bg-[#FFFFFF] border-stone-300 text-stone-900 [color-scheme:light] w-full sm:w-48" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-stone-600 text-xs">Sede</Label>
          <Select value={sede} onValueChange={setSede}>
            <SelectTrigger data-testid="rooms-sede-select" className="bg-[#FFFFFF] border-stone-300 text-stone-900 w-full sm:w-56"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">
              {config.sedi?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-amber-600 animate-spin" /></div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="room-schedule-grid">
          {rooms.map((room) => {
            const roomShifts = shifts.filter((s) => s.room === room && s.sede === sede).sort((a, b) => a.start_time.localeCompare(b.start_time));
            return (
              <div key={room} className="card-luxe rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <DoorOpen className="w-5 h-5 text-amber-600" />
                    <h3 className="font-serif text-lg text-stone-900">{room}</h3>
                  </div>
                  <Button size="sm" onClick={() => openFor(room)} data-testid={`add-shift-${room.replace(/\s+/g, "-").toLowerCase()}`}
                    className="bg-amber-500/90 hover:bg-amber-400 text-[#221C12] h-8">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {roomShifts.length === 0 ? (
                  <p className="text-sm text-stone-500 py-3">Nessun turno assegnato</p>
                ) : (
                  <div className="space-y-2">
                    {roomShifts.map((s) => (
                      <div key={s.id} className="flex items-center justify-between rounded-lg bg-[#F4EFE4] border border-stone-200 px-3 py-2.5" data-testid={`shift-item-${s.id}`}>
                        <div>
                          <div className="flex items-center gap-2 text-sm text-amber-700 font-mono-legal"><Clock className="w-3.5 h-3.5" /> {s.start_time} - {s.end_time}</div>
                          <div className="text-sm text-stone-800 mt-0.5">{s.collaborator_name}</div>
                          {s.note && <div className="text-xs text-stone-500">{s.note}</div>}
                        </div>
                        <button onClick={() => remove(s.id)} data-testid={`delete-shift-${s.id}`} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border border-amber-500/20 text-stone-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">Assegna turno · {form.room}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-stone-600 text-sm">Collaboratore</Label>
              <Select value={form.collaborator_name} onValueChange={(v) => setForm((f) => ({ ...f, collaborator_name: v }))}>
                <SelectTrigger data-testid="shift-collaborator-select" className="bg-[#F4EFE4] border-stone-300 text-stone-900"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">
                  {collaborators.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-stone-600 text-sm">Dalle</Label>
                <Select value={form.start_time} onValueChange={(v) => setForm((f) => ({ ...f, start_time: v }))}>
                  <SelectTrigger className="bg-[#F4EFE4] border-stone-300 text-stone-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-stone-600 text-sm">Alle</Label>
                <Select value={form.end_time} onValueChange={(v) => setForm((f) => ({ ...f, end_time: v }))}>
                  <SelectTrigger className="bg-[#F4EFE4] border-stone-300 text-stone-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-stone-600 text-sm">Note</Label>
              <Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="bg-[#F4EFE4] border-stone-300 text-stone-900" placeholder="Opzionale" />
            </div>
            <Button onClick={save} disabled={saving} data-testid="save-shift-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assegna Turno"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
